import { useMemo } from 'react';
import { InfoCard } from '@backstage/core-components';
import { useEntity } from '@backstage/plugin-catalog-react';
import { RiDatabase2Line, RiDownloadLine, RiFileCopyLine } from '@remixicon/react';
import {
  Button,
  CopyButton,
  CopyableValue,
  ExternalLink,
  Flex,
  InfraCodeBlock,
  InfraDivider,
  InfraField,
  InfraSection,
  InfraStack,
  InfraTag,
  InfraTagValue,
  Text,
  infraCardStyles as styles,
} from '../ui/InfraPrimitives';

export const RedisEnterpriseInfoCard = () => {
  const { entity } = useEntity();
  const annotations = entity.metadata.annotations || {};

  const templateType = annotations['infrastructure.backstage.io/type'] || '';
  const provider = annotations['infrastructure.backstage.io/provider'] || '';
  const isAWS = provider === 'aws';
  const isGCP = provider === 'gcp';

  const uiURL = annotations['redis-enterprise.infrastructure/ui-url'];
  const apiURL = annotations['redis-enterprise.infrastructure/api-url'];
  const redisVersion = annotations['redis-enterprise.infrastructure/version'];
  const dnsZone = annotations['redis-enterprise.infrastructure/dns-zone'];
  const clusterFqdn = annotations['redis-enterprise.infrastructure/cluster-fqdn'];
  const nodePublicIPs = annotations['redis-enterprise.infrastructure/node-public-ips'];
  const nodePrivateIPs = annotations['redis-enterprise.infrastructure/node-private-ips'];
  const nodeCount = annotations['redis-enterprise.infrastructure/node-count'];
  const instanceType = annotations['redis-enterprise.infrastructure/instance-type'];

  const awsRegion = annotations['aws.infrastructure/region'];
  const gcpRegion = annotations['gcp.infrastructure/region'];
  const gcpZone = annotations['gcp.infrastructure/zone'];
  const region = awsRegion || gcpRegion;

  const awsVpcId = annotations['aws.infrastructure/vpc-id'];
  const gcpNetwork = annotations['gcp.infrastructure/network'];
  const vpcId = awsVpcId || gcpNetwork;

  const gcpProjectId = annotations['gcp.infrastructure/project-id'];
  const gcpConsoleUrls = annotations['gcp.infrastructure/console-urls'];
  const gcpGcloudSshCommands = annotations['gcp.infrastructure/gcloud-ssh-commands'];

  const awsConsoleUrls = annotations['aws.infrastructure/console-urls'];

  const sshPrivateKeyEncoded = annotations['redis-enterprise.infrastructure/ssh-private-key'];
  const sshCommandsStr = annotations['redis-enterprise.infrastructure/ssh-commands'];

  const hasRedisEnterpriseInfo = uiURL || dnsZone || nodePublicIPs;

  if (templateType === 'gcp-gemfire') {
    return null;
  }

  if (!hasRedisEnterpriseInfo) {
    return null;
  }

  const adminUser = isGCP
    ? (clusterFqdn ? `admin@${clusterFqdn}` : 'admin@redis.local')
    : (dnsZone ? `admin@${dnsZone}` : 'admin@redis.local');

  const publicIPs = nodePublicIPs?.split(',') || [];
  const privateIPs = nodePrivateIPs?.split(',') || [];

  const sshUser = isGCP ? 'cloud-user' : 'ec2-user';
  const masterIP = publicIPs[0] || privateIPs[0] || '<master-ip>';
  const passwordRetrievalCmd = `ssh -i ansible_key.pem ${sshUser}@${masterIP} sudo cat /opt/redislabs/.cluster_admin_password`;

  const sshPrivateKey = useMemo(() => {
    if (!sshPrivateKeyEncoded) return '';
    try {
      return window.atob(sshPrivateKeyEncoded);
    } catch (e) {
      console.error('Failed to decode SSH key:', e);
      return '';
    }
  }, [sshPrivateKeyEncoded]);

  const sshCommands = sshCommandsStr?.split('|||') || [];
  const gcpConsoleUrlsList = gcpConsoleUrls?.split('|||') || [];
  const gcpGcloudSshCommandsList = gcpGcloudSshCommands?.split('|||') || [];
  const awsConsoleUrlsList = awsConsoleUrls?.split('|||') || [];

  const downloadSSHKey = () => {
    const element = document.createElement('a');
    const file = new Blob([sshPrivateKey], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = 'ansible_key.pem';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const curlExample = `# Replace <paste-password-here> with the password retrieved above.
curl -k -u ${adminUser}:<paste-password-here> \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "demo-db",
    "type": "redis",
    "memory_size": 1073741824,
    "port": 12000,
    "replication": true
  }' \\
  ${apiURL || 'https://<node-ip>:9443'}/v1/bdbs`;

  return (
    <InfoCard
      title="Redis Enterprise Cluster Information"
      icon={<RiDatabase2Line size={20} />}
    >
      <InfraStack>
        <InfraSection title="☁️ Cluster Configuration">
          {dnsZone && <InfraField label="DNS Zone:" value={dnsZone} />}
          {nodeCount && (
            <InfraField
              label="Node Count:"
              value={<InfraTagValue>{nodeCount}</InfraTagValue>}
              mono={false}
            />
          )}
          {instanceType && (
            <InfraField
              label={isGCP ? 'Machine Type:' : 'Instance Type:'}
              value={<InfraTagValue>{instanceType}</InfraTagValue>}
              mono={false}
            />
          )}
          {redisVersion && (
            <InfraField
              label="Redis Version:"
              value={<InfraTagValue>{redisVersion}</InfraTagValue>}
              mono={false}
            />
          )}
          {region && (
            <InfraField
              label={isGCP ? 'GCP Region:' : 'AWS Region:'}
              value={<InfraTagValue>{region}</InfraTagValue>}
              mono={false}
            />
          )}
          {isGCP && gcpZone && (
            <InfraField
              label="GCP Zone:"
              value={<InfraTagValue>{gcpZone}</InfraTagValue>}
              mono={false}
            />
          )}
          {vpcId && (
            <InfraField
              label={isGCP ? 'VPC Network:' : 'VPC ID:'}
              value={vpcId}
            />
          )}
          {isGCP && gcpProjectId && (
            <InfraField label="GCP Project:" value={gcpProjectId} />
          )}
        </InfraSection>

        <InfraSection title="🌐 Access URLs">
          {uiURL && (
            <InfraField
              label="Web UI:"
              value={
                <span className={styles.copyRow}>
                  <ExternalLink href={uiURL}>{uiURL}</ExternalLink>
                  <CopyButton text={uiURL} />
                </span>
              }
              mono={false}
            />
          )}
          {apiURL && (
            <InfraField
              label="REST API:"
              value={
                <span className={styles.copyRow}>
                  <ExternalLink href={apiURL}>{apiURL}</ExternalLink>
                  <CopyButton text={apiURL} />
                </span>
              }
              mono={false}
            />
          )}
        </InfraSection>

        {(publicIPs.length > 0 || privateIPs.length > 0) && (
          <InfraSection title="🖥️ Node IP Addresses">
            {publicIPs.length > 0 && (
              <InfraField
                label="Public IPs:"
                value={
                  <span className={styles.copyRow}>
                    <span className={styles.value}>{publicIPs.join(', ')}</span>
                    <CopyButton text={publicIPs.join(', ')} />
                  </span>
                }
                mono={false}
              />
            )}
            {privateIPs.length > 0 && (
              <InfraField
                label="Private IPs:"
                value={
                  <span className={styles.copyRow}>
                    <span className={styles.value}>{privateIPs.join(', ')}</span>
                    <CopyButton text={privateIPs.join(', ')} />
                  </span>
                }
                mono={false}
              />
            )}
          </InfraSection>
        )}

        <InfraDivider />
        <InfraSection title="🔑 Cluster Admin Credentials">
          <CopyableValue label="Username:" value={adminUser} />
          <InfraField
            label="Password:"
            value="generated & stored on master node"
          />
          <Text variant="title-small" style={{ marginTop: '0.75rem' }}>
            Retrieve password:
          </Text>
          <Flex gap="2" alignItems="center">
            <div style={{ flex: 1, position: 'relative' }}>
              <InfraCodeBlock variant="light">{passwordRetrievalCmd}</InfraCodeBlock>
            </div>
            <CopyButton text={passwordRetrievalCmd} />
          </Flex>
          <Text variant="body-small" style={{ marginTop: '0.75rem', display: 'block' }}>
            💡 <strong>Tip:</strong> The cluster admin password is generated at provisioning time
            and stored on the master node at{' '}
            <code>/opt/redislabs/.cluster_admin_password</code>. Download the SSH key below,
            then run the retrieval command. Database creation is disabled by default — create
            databases via UI or REST API.
          </Text>
        </InfraSection>

        {sshPrivateKey && (
          <>
            <InfraDivider />
            <InfraSection title="🔐 SSH Access">
              <Text variant="title-small">SSH Private Key:</Text>
              <Flex gap="2">
                <Button variant="primary" size="small" onPress={downloadSSHKey}>
                  <RiDownloadLine size={16} />
                  Download Key
                </Button>
                <Button
                  variant="secondary"
                  size="small"
                  onPress={() => navigator.clipboard.writeText(sshPrivateKey)}
                >
                  <RiFileCopyLine size={16} />
                  Copy Key
                </Button>
              </Flex>

              {sshCommands.length > 0 && (
                <>
                  <Text variant="title-small" style={{ marginTop: '0.75rem' }}>
                    SSH Commands:
                  </Text>
                  {sshCommands.map((cmd, index) => (
                    <Flex key={index} gap="2" alignItems="center" style={{ marginBottom: '0.5rem' }}>
                      <div style={{ flex: 1 }}>
                        <InfraCodeBlock variant="light">{cmd}</InfraCodeBlock>
                      </div>
                      <CopyButton text={cmd} />
                    </Flex>
                  ))}
                </>
              )}

              <Text variant="body-small" style={{ marginTop: '0.75rem', display: 'block' }}>
                💡 <strong>Important:</strong> After downloading, run{' '}
                <code>chmod 400 ansible_key.pem</code> to fix permissions, then use the SSH
                commands above.
              </Text>
              <Text variant="body-small" style={{ marginTop: '0.5rem', display: 'block' }}>
                ⚠️ <strong>Without chmod 400:</strong> SSH will reject the key with
                &quot;UNPROTECTED PRIVATE KEY FILE&quot; error.
              </Text>
            </InfraSection>
          </>
        )}

        {isGCP && (gcpConsoleUrlsList.length > 0 || gcpGcloudSshCommandsList.length > 0) && (
          <>
            <InfraDivider />
            <InfraSection title="☁️ GCP Console & gcloud Access">
              {gcpConsoleUrlsList.length > 0 && (
                <>
                  <Text variant="title-small">GCP Console URLs:</Text>
                  {gcpConsoleUrlsList.map((url, index) => (
                    <InfraField
                      key={`console-${index}`}
                      label={`Node ${index + 1}:`}
                      value={
                        <span className={styles.copyRow}>
                          <ExternalLink href={url}>Node {index + 1} Console</ExternalLink>
                          <CopyButton text={url} />
                        </span>
                      }
                      mono={false}
                    />
                  ))}
                </>
              )}

              {gcpGcloudSshCommandsList.length > 0 && (
                <>
                  <Text variant="title-small" style={{ marginTop: '0.75rem' }}>
                    gcloud SSH Commands:
                  </Text>
                  {gcpGcloudSshCommandsList.map((cmd, index) => (
                    <Flex key={`gcloud-${index}`} gap="2" alignItems="center" style={{ marginBottom: '0.5rem' }}>
                      <div style={{ flex: 1 }}>
                        <InfraCodeBlock variant="light">{cmd}</InfraCodeBlock>
                      </div>
                      <CopyButton text={cmd} />
                    </Flex>
                  ))}
                </>
              )}

              <Text variant="body-small" style={{ marginTop: '0.75rem', display: 'block' }}>
                💡 <strong>Note:</strong> gcloud commands require the Google Cloud SDK to be
                installed and authenticated.
              </Text>
            </InfraSection>
          </>
        )}

        {isAWS && awsConsoleUrlsList.length > 0 && (
          <>
            <InfraDivider />
            <InfraSection title="☁️ AWS Console Access">
              <Text variant="title-small">AWS Console URLs:</Text>
              {awsConsoleUrlsList.map((url, index) => (
                <InfraField
                  key={`aws-console-${index}`}
                  label={`Node ${index + 1}:`}
                  value={
                    <span className={styles.copyRow}>
                      <ExternalLink href={url}>Node {index + 1} Console</ExternalLink>
                      <CopyButton text={url} />
                    </span>
                  }
                  mono={false}
                />
              ))}
              <Text variant="body-small" style={{ marginTop: '0.75rem', display: 'block' }}>
                💡 <strong>Note:</strong> Click the links above to view instances in the AWS
                Console.
              </Text>
            </InfraSection>
          </>
        )}

        <InfraDivider />
        <InfraSection title="🚀 Quick Start">
          <Text variant="title-small">1. Access the Web UI:</Text>
          <InfraCodeBlock variant="light">{uiURL || 'https://<node-ip>:8443'}</InfraCodeBlock>

          <Text variant="title-small" style={{ marginTop: '0.75rem' }}>
            2. Create a database via REST API:
          </Text>
          <InfraCodeBlock variant="light">{curlExample}</InfraCodeBlock>

          <Text variant="title-small" style={{ marginTop: '0.75rem' }}>
            3. Connect to your database:
          </Text>
          <InfraCodeBlock variant="light">
            redis-cli -h {dnsZone ? `redis-12000.${dnsZone}` : '<database-endpoint>'} -p 12000
          </InfraCodeBlock>
        </InfraSection>
      </InfraStack>
    </InfoCard>
  );
};
